import { LoadingStatus, Product } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  AddToCartButton,
  Image,
  LoadingSkeleton,
  PriceInfo,
  ProductCampaignChip,
  ProductPackageChip,
  QuantitySelector,
  SmartLink,
  Title,
  WishlistToggle,
} from '@components';
import { useAppSelector, useAuth } from '@hooks';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { Alert, Box, Stack } from '@mui/material';
import { styled } from '@mui/system';
import { getCartQuantity } from '@store/cart/cart.slice';
import { colors } from '@themes/colors';
import { breakpoints, spacing } from '@themes/theme.helpers';
import { t } from '@translations';
import { checkIfExpired, ErpStatus, getErpStatus } from '@utils/products.utils';
import { useState } from 'react';
// STYLES
//--------
const ProductCardWrapper = styled(Box)`
  background-color: ${colors.secondary.lightBlue};
  border-radius: 4px;
  text-align: left;
  height: 100%;
`;

const ProductWrapperStack = styled(Stack)`
  height: 100%;
  position: relative;
`;

const ImageWrapper = styled('div')`
  display: flex;
  height: 350px;
  overflow: hidden;
  text-align: center;
  padding: ${spacing(4)};
  background: ${colors.background.white};
  justify-content: center;
  align-items: center;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;

  img {
    object-fit: contain;
    height: 100%;
    width: 100%;
  }

  ${breakpoints.down('md')} {
    padding: 8px;
    height: 200px;
  }
  ${breakpoints.down('sm')} {
    height: 140px;
  }
`;

const MainContentBox = styled(Stack)`
  height: 100%;
  padding: 0 16px;
  padding-bottom: 16px;
  ${breakpoints.down('md')} {
    padding: 0 8px 8px;
    h4,
    .current-price-box {
      font-size: 16px;
    }
  }
`;

const MobileChipWrapper = styled(Box)`
  position: absolute;
  left: ${spacing(1)};
  top: ${spacing(1)};
  display: flex;
  flex-direction: row;
  gap: ${spacing(0.5)};
  ${breakpoints.up('md')} {
    display: none;
  }
`;

const DesktopChipWrapper = styled(Box)`
  position: absolute;
  left: ${spacing(2)};
  top: ${spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(0.5)};
  place-items: flex-start;
  ${breakpoints.down('md')} {
    display: none;
  }
`;

const WishlistToggleWrapper = styled(Box)`
  position: absolute;
  right: ${spacing(1)};
  top: ${spacing(1)};
  ${breakpoints.down('md')} {
    right: -4px;
    top: -4px;
  }
`;

const CartWrapper = styled(Stack)`
  ${breakpoints.down('sm')} {
    & .quantity-box {
      display: none;
    }
  }
`;
// TYPES
//-------
export type ProductCardProps = LoadingStatus & Product;

// COMPONENTS
//------------
export const ProductCard = ({
  isLoading,
  name,
  image,
  url,
  grossPrice,
  discountPrice,
  code = '',
  packageIncluded,
  productLabel,
  itemERPDescription,
  itemERPItemStatus,
}: ProductCardProps) => {
  const { isAuthenticated, isBeremaUser, isDealerUser } = useAuth();
  const erpStatus = getErpStatus(itemERPItemStatus as string) as ErpStatus;
  const isExpired = checkIfExpired(erpStatus);
  const shouldDisplayAddToCart =
    code && !isLoading && isAuthenticated && !isExpired && !isBeremaUser && discountPrice?.amount;
  const cartQuantity = useAppSelector(getCartQuantity(code));
  const [quantity, setQuantity] = useState(1);
  const displayName = itemERPDescription ? itemERPDescription : name;
  const isCampaignProduct = productLabel?.toLocaleLowerCase() === 'kampanje';
  const shouldDisplayChips = packageIncluded || shouldDisplayAddToCart || isCampaignProduct;
  return (
    <ProductCardWrapper className='hover-shadow'>
      <ProductWrapperStack
        gap={{
          xs: 1,
          md: 2,
        }}
      >
        <SmartLink
          href={url}
          disabled={isLoading}
          sx={{
            position: 'relative',
          }}
        >
          <ImageWrapper>
            {isLoading ? (
              <LoadingSkeleton sx={{ width: '100%', height: '100%' }} />
            ) : (
              <Image
                image={image}
                isLoading={isLoading}
                alt={displayName}
                title={displayName}
                resizerParams={{ width: 400, format: 'webp' }}
              />
            )}
          </ImageWrapper>
        </SmartLink>
        {shouldDisplayChips ? (
          <DesktopChipWrapper>
            {isCampaignProduct ? <ProductCampaignChip /> : null}
            {packageIncluded ? <ProductPackageChip /> : null}
            {shouldDisplayAddToCart ? <CartQuantityChip cartQuantity={cartQuantity} /> : null}
          </DesktopChipWrapper>
        ) : null}

        {shouldDisplayChips ? (
          <MobileChipWrapper>
            {isCampaignProduct ? <ProductCampaignChip showIcon /> : null}
            {packageIncluded ? <ProductPackageChip showIcon /> : null}
            {shouldDisplayAddToCart ? <CartQuantityChip cartQuantity={cartQuantity} /> : null}
          </MobileChipWrapper>
        ) : null}
        {!isLoading && isAuthenticated && isDealerUser ? (
          <WishlistToggleWrapper>
            <WishlistToggle
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
              code={code}
              name={displayName}
            />
          </WishlistToggleWrapper>
        ) : null}
        <MainContentBox gap={2}>
          <SmartLink href={url} disabled={isLoading} sx={{ height: '100%' }}>
            <Title variant='h4' regular noDot className='line-clamp-2'>
              {isLoading ? <LoadingSkeleton sx={{ height: '20px' }} /> : displayName}
            </Title>
          </SmartLink>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            <SmartLink href={url} disabled={isLoading}>
              <PriceInfo
                isExpired={isExpired}
                isLoading={isLoading}
                grossPrice={grossPrice}
                discountPrice={discountPrice}
                sx={{
                  '& .your-price-wrapper': {
                    fontSize: {
                      md: '24px',
                      sx: '16px',
                    },
                  },
                }}
              />
            </SmartLink>
          </Box>
          {shouldDisplayAddToCart ? (
            <CartWrapper
              direction='row'
              gap={{
                xs: 1,
                md: 2,
              }}
            >
              <QuantitySelector setQuantity={setQuantity} quantity={quantity} />
              <AddToCartButton code={code} name={displayName} quantity={quantity} />
            </CartWrapper>
          ) : null}
        </MainContentBox>
      </ProductWrapperStack>
    </ProductCardWrapper>
  );
};

export type CartQuantityChipProps = {
  cartQuantity?: number;
};
export const CartQuantityChip = ({ cartQuantity = 0 }: CartQuantityChipProps) => {
  return cartQuantity > 0 ? (
    <Alert
      severity='info'
      icon={false}
      sx={{
        p: 0,
        '& .MuiAlert-message': {
          padding: '4px',
          fontSize: {
            md: '16px',
            xs: '14px',
          },
        },
      }}
    >
      <Stack
        direction='row'
        gap={{
          xs: 0.5,
          md: 1,
        }}
        alignItems='center'
      >
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
          }}
        >
          {t(TextCodes.Product.Cart.NumberInCart, { cartQuantity: cartQuantity.toString() })}
        </Box>
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
          }}
        >
          {cartQuantity.toString()}
        </Box>
        <ShoppingCartCheckoutIcon sx={{ fontSize: '16px' }} />
      </Stack>
    </Alert>
  ) : null;
};
